import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { ExcelGrid, exportToCsv, type ExcelColumnDef } from '@/components/excelGrid'
import { listStockMovements } from '@/services/inventory/apiClient'
import type { StockMovementDto } from '@/services/inventory/types'
import { getMaterialGroup } from '@/domain/materialGroups'
import { DateInput } from '@/components/forms/DateInput'
import { formatDateTimeFull } from '@/lib/dateFormat'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'

export interface EnrichedMovementRow extends StockMovementDto {
  after?: number
  material_name?: string
  group_code?: string | null
  group_name?: string
}

export function getMovementTypeLabel(type: string): string {
  switch (type) {
    case 'RECEIPT':
      return 'Nhập kho (PO)'
    case 'INBOUND':
      return 'Nhập kho'
    case 'ISSUE':
      return 'Xuất kho'
    case 'RETURN_TO_SUPPLIER':
      return 'Xuất kho (Trả NCC)'
    case 'ADJUSTMENT':
      return 'Điều chỉnh'
    case 'RECEIPT_CORRECTION':
      return 'Sửa phiếu nhập'
    case 'INBOUND_CORRECTION':
      return 'Sửa phiếu nhập'
    case 'ISSUE_CORRECTION':
      return 'Sửa phiếu xuất'
    case 'RETURN_CORRECTION':
      return 'Sửa phiếu trả NCC'
    case 'CUSTOMER_RETURN':
      return 'Khách trả'
    case 'REWORK_IN':
      return 'Nhập tái chế'
    case 'STATUS_TRANSFER':
      return 'Chuyển trạng thái'
    case 'FREE_CONVERT_IN':
      return 'Nhập chuyển đổi CHT'
    case 'FREE_CONVERT_IN_CORRECTION':
      return 'Sửa chuyển đổi CHT'
    case 'CONVERT_OUT':
      return 'Chuyển đổi VT chuẩn'
    case 'CONVERT_CORRECTION':
      return 'Sửa chuyển đổi CHT'
    default:
      return type
  }
}

export function getDocLink(row: StockMovementDto): string | null {
  const code = (row.doc_code || '').trim()
  const type = (row.type || '').trim()

  if (code.startsWith('PO-') && row.doc_id) {
    return `/purchasing/po/${row.doc_id}`
  }
  if (code.startsWith('IS-') || code.startsWith('ISL-')) {
    return `/warehouse/issue-slips`
  }
  if (code.startsWith('RCV-') || code.startsWith('INB-') || code.startsWith('F-RCV-')) {
    return `/warehouse/receipts`
  }
  if (
    code.startsWith('ISS-') ||
    code.startsWith('XK-') ||
    code.startsWith('PXK-') ||
    code.startsWith('RET-') ||
    code.startsWith('TRA-')
  ) {
    return `/warehouse/issues`
  }
  if (code.startsWith('ADJ-')) {
    return `/warehouse/stocktakes`
  }

  // Fallbacks by type
  if (type === 'RECEIPT' || type === 'INBOUND') {
    return `/warehouse/receipts`
  }
  if (type === 'ISSUE' || type === 'RETURN_TO_SUPPLIER') {
    return `/warehouse/issues`
  }
  if (type === 'ADJUSTMENT') {
    return `/warehouse/stocktakes`
  }
  return null
}

export function TransactionsPage() {
  const [movements, setMovements] = useState<StockMovementDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [groupFilter, setGroupFilter] = useState('')
  const [clearFilterCounter, setClearFilterCounter] = useState(0)
  const [resetWidthsCounter, setResetWidthsCounter] = useState(0)
  const [activeFilterCount, setActiveFilterCount] = useState(0)

  // Debounce search query to reduce network calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const movList = await listStockMovements({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        type: typeFilter || undefined,
        group_code: groupFilter || undefined,
        search: debouncedSearch || undefined,
        include_free_stock: true,
      })
      setMovements(movList)
    } catch (err: any) {
      setError(err.message || 'Lỗi tải lịch sử biến động kho')
    } finally {
      setLoading(false)
    }
  }, [dateFrom, dateTo, typeFilter, groupFilter, debouncedSearch])

  useEffect(() => {
    void loadData()
  }, [loadData])

  // Map server movements directly to enriched rows using SQL window function balance
  const enrichedRows = useMemo<EnrichedMovementRow[]>(() => {
    return movements.map((item) => {
      const groupCode = item.group_code || null
      const isFree = Boolean(item.is_free_stock || groupCode === 'free')
      const groupName = isFree
        ? 'Hàng tự do'
        : groupCode
        ? getMaterialGroup(groupCode)?.name || 'Chưa phân nhóm'
        : 'Chưa phân nhóm'

      return {
        ...item,
        after: item.after_qty ?? item.after ?? 0,
        material_name: item.material_name || (isFree ? 'Hàng tự do' : 'Vật tư'),
        group_code: groupCode,
        group_name: groupName,
        base_uom: item.base_uom || '',
      }
    })
  }, [movements])

  // Column definitions matching demo
  const columns = useMemo<ExcelColumnDef<EnrichedMovementRow>[]>(
    () => [
      {
        k: 'at',
        h: 'Ngày giờ',
        w: 110,
        ctr: true,
        get: (x) => formatDateTimeFull(x.at),
        sv: (x) => x.at,
        renderCell: (x) => <span title={formatDateTimeFull(x.at)}>{formatDateTimeFull(x.at)}</span>,
      },
      {
        k: 'type',
        h: 'Loại',
        w: 150,
        get: (x) => getMovementTypeLabel(x.type),
      },
      {
        k: 'doc',
        h: 'Chứng từ',
        w: 130,
        get: (x) => x.doc_code,
        renderCell: (x) => {
          const link = getDocLink(x)
          if (link) {
            return (
              <Link
                to={link}
                className="text-[#0066cc] hover:underline font-mono"
                title={`Mở chứng từ ${x.doc_code}`}
              >
                {x.doc_code}
              </Link>
            )
          }
          return <span className="font-mono">{x.doc_code}</span>
        },
      },
      {
        k: 'g',
        h: 'Nhóm',
        w: 140,
        get: (x) => x.group_name || 'Chưa phân nhóm',
        renderCell: (x) => {
          if (x.is_free_stock || x.group_code === 'free') {
            return (
              <span className="inline-block px-1.5 py-0.5 rounded text-[11px] bg-amber-50 text-amber-800 border border-amber-200">
                Hàng tự do
              </span>
            )
          }
          return <span>{x.group_name || 'Chưa phân nhóm'}</span>
        },
      },
      {
        k: 'n',
        h: 'Tên vật tư',
        w: 220,
        get: (x) => x.material_name || '',
      },
      {
        k: 'u',
        h: 'ĐVT',
        w: 60,
        ctr: true,
        get: (x) => x.base_uom || '',
      },
      {
        k: 'q',
        h: 'Biến động',
        w: 100,
        num: true,
        get: (x) => x.qty_base,
        render: (x) => {
          const isNeg = x.qty_base < 0
          const formatted =
            (x.qty_base > 0 ? '+' : '') +
            x.qty_base.toLocaleString('vi-VN', { maximumFractionDigits: 2 })
          return (
            <span className={isNeg ? 'text-[#b30b0b] font-medium' : 'text-[#1e7e34] font-medium'}>
              {formatted}
            </span>
          )
        },
      },
      {
        k: 'after',
        h: 'Tồn sau',
        w: 90,
        num: true,
        get: (x) => x.after ?? x.after_qty ?? 0,
      },
      {
        k: 'by',
        h: 'Người',
        w: 130,
        get: (x) => x.by,
      },
      {
        k: 'note',
        h: 'Ghi chú / lý do',
        w: 300,
        get: (x) => x.note || '',
      },
    ],
    [],
  )

  const handleClearFilters = () => {
    setSearchQuery('')
    setDateFrom('')
    setDateTo('')
    setTypeFilter('')
    setGroupFilter('')
    setClearFilterCounter((c) => c + 1)
  }

  const handleExportCsv = () => {
    const headers = [
      'Ngày giờ',
      'Loại',
      'Chứng từ',
      'Nhóm',
      'Tên vật tư',
      'ĐVT',
      'Biến động',
      'Tồn sau',
      'Người',
      'Ghi chú / lý do',
    ]
    const rows = enrichedRows.map((r) => [
      r.at,
      getMovementTypeLabel(r.type),
      r.doc_code,
      r.group_name || '',
      r.material_name || '',
      r.base_uom || '',
      r.qty_base,
      r.after ?? r.after_qty ?? '',
      r.by,
      r.note || '',
    ])
    exportToCsv('kho-lich-su-bien-dong', headers, rows)
  }

  const hasActiveFilters = Boolean(
    searchQuery || dateFrom || dateTo || typeFilter || groupFilter || activeFilterCount > 0,
  )

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white text-[#222]">
      <div className="px-3 pt-2">
        <Breadcrumb items={[{ label: 'Kho', to: '/warehouse/stock' }, { label: 'Lịch sử biến động' }]} />
        <PageHeader title="Lịch sử biến động kho" subtitle="Sổ cái kho · chỉ thêm, không sửa" />
      </div>
      {/* Top Bar */}
      <div className="flex items-center gap-2 flex-wrap px-2.5 py-1.5 border-b border-[#ddd] bg-[#fafafa]">
        <input
          type="search"
          placeholder="Tìm vật tư, chứng từ, người…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border border-[#999] px-2 py-1 text-[12px] bg-white rounded-[2px] w-[220px] outline-none"
        />

        <div className="flex items-center gap-1.5 text-[12px] text-[#444]">
          <span>Từ</span>
          <DateInput
            value={dateFrom}
            onChange={(val) => setDateFrom(val)}
            className="w-[125px] h-[26px] text-[12px]"
          />
          <span>đến</span>
          <DateInput
            value={dateTo}
            onChange={(val) => setDateTo(val)}
            className="w-[125px] h-[26px] text-[12px]"
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border border-[#999] px-2 py-1 text-[12px] bg-white rounded-[2px] outline-none h-[26px] text-[#222]"
          title="Lọc theo loại biến động"
        >
          <option value="">Tất cả loại</option>
          <option value="RECEIPT">Nhập kho (PO)</option>
          <option value="INBOUND">Nhập kho</option>
          <option value="ISSUE">Xuất kho</option>
          <option value="RETURN_TO_SUPPLIER">Xuất kho (Trả NCC)</option>
          <option value="ADJUSTMENT">Điều chỉnh</option>
          <option value="RECEIPT_CORRECTION">Sửa phiếu nhập</option>
          <option value="ISSUE_CORRECTION">Sửa phiếu xuất</option>
          <option value="RETURN_CORRECTION">Sửa phiếu trả NCC</option>
          <option value="CUSTOMER_RETURN">Khách trả</option>
          <option value="REWORK_IN">Nhập tái chế</option>
          <option value="STATUS_TRANSFER">Chuyển trạng thái</option>
          <option value="FREE_CONVERT_IN">Nhập chuyển đổi CHT</option>
          <option value="CONVERT_OUT">Chuyển đổi VT chuẩn</option>
        </select>

        <select
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          className="border border-[#999] px-2 py-1 text-[12px] bg-white rounded-[2px] outline-none h-[26px] text-[#222]"
          title="Lọc theo nhóm vật tư"
        >
          <option value="">Tất cả nhóm</option>
          <option value="go">Phôi gỗ</option>
          <option value="van">Ván công nghiệp</option>
          <option value="nk">Ngũ kim & phụ kiện</option>
          <option value="bb">Bao bì</option>
          <option value="td">Vật tư thông dụng</option>
          <option value="free">Hàng tự do</option>
        </select>

        <div className="flex-1" />

        <button
          type="button"
          disabled={!hasActiveFilters}
          onClick={handleClearFilters}
          className="border border-[#999] bg-white hover:bg-[#eee] disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded-[2px] text-[12px]"
        >
          ✕ Bỏ lọc
        </button>

        <button
          type="button"
          onClick={() => setResetWidthsCounter((c) => c + 1)}
          title="Đặt lại độ rộng các cột về mặc định"
          className="border border-[#999] bg-white hover:bg-[#eee] px-2.5 py-1 rounded-[2px] text-[12px]"
        >
          ↔ Đặt lại độ rộng
        </button>

        <button
          type="button"
          onClick={handleExportCsv}
          className="border border-[#999] bg-white hover:bg-[#eee] px-2.5 py-1 rounded-[2px] text-[12px]"
        >
          ⬇ Excel
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 p-2 text-xs text-red-700">
          {error}
        </div>
      )}

      {/* Grid container */}
      <div className="flex-1 min-h-0 relative">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-[#666] text-xs">
            Đang tải dữ liệu sổ cái biến động kho…
          </div>
        ) : (
          <ExcelGrid<EnrichedMovementRow>
            id="his"
            data={enrichedRows}
            columns={columns}
            freeze={2}
            searchQuery={searchQuery}
            clearFilterTrigger={clearFilterCounter}
            resetWidthsTrigger={resetWidthsCounter}
            onFilterChange={(cnt) => setActiveFilterCount(cnt)}
            emptyMessage={
              movements.length === 0
                ? 'Chưa có biến động kho nào'
                : 'Không có dòng nào khớp bộ lọc'
            }
          />
        )}
      </div>
    </div>
  )
}
